import { useCallback, useEffect, useState } from "react";
import type { MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { demoApi, problemMessage } from "@/api/client";
import { useSelectedEntity } from "@/hooks/useSelectedEntity";
import { formatMoney } from "@/lib/format";
import type { IncomeStatementReport } from "@/api/types";
import "./ReportsIncomeStatement.css";

type ReportStatus = "loading" | "ready" | "error";
type Basis = "cash" | "accrual";

const BASES: { key: Basis; label: string }[] = [
  { key: "cash", label: "Cash" },
  { key: "accrual", label: "Accrual" },
];

/**
 * Income statement by entity — Reports' entity-scoped profit &amp; loss, ef-053-1.
 * ADR 0009 §6 ("Reports is one picker" — a report renders inside the template at
 * `/reports/{slug}`, no area tab strip of its own; "All reports" below is the only way
 * back).
 *
 * <p>
 * ENTITY IS RESOLVED IMPLICITLY, NO PICKER — the same shared, persisted switcher
 * (`useSelectedEntity`) every Farm screen already reads (`Milk.tsx`, `Grain.tsx`,
 * `Fields.tsx`). `IncomeStatementReportEndpoints` requires `entityId` (D2, unlike every
 * whole-farm-default report above) — there is no server-side fallback to omit it and
 * still get an answer, so this screen supplies it from the switcher rather than adding a
 * second, redundant picker of its own.
 * </p>
 *
 * <p>
 * NO FROM/TO, NO APPLY BUTTON — an income statement is a point-in-time cumulative total
 * (same "fetch on mount" shape `ReportsLivestockInventory.tsx`'s own D4 note gives). The
 * Cash/Accrual toggle is the only control that refetches.
 * </p>
 */
export function ReportsIncomeStatement() {
  const { entityId, status: entityStatus } = useSelectedEntity();
  const [basis, setBasis] = useState<Basis>("cash");
  const [report, setReport] = useState<IncomeStatementReport | null>(null);
  const [status, setStatus] = useState<ReportStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (id: string, signal?: AbortSignal): Promise<void> => {
      setStatus("loading");
      setError(null);
      try {
        const result = await demoApi.getIncomeStatementReport({ entityId: id, basis }, signal);
        setReport(result);
        setStatus("ready");
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setReport(null);
        setStatus("error");
        setError(problemMessage(err, "Could not load the income statement."));
      }
    },
    [basis],
  );

  useEffect(() => {
    if (entityStatus === "loading") return;
    if (entityId === null) {
      setReport(null);
      setStatus("error");
      setError("This farm has no active entity to report on.");
      return;
    }
    const controller = new AbortController();
    void load(entityId, controller.signal);
    return () => controller.abort();
  }, [load, entityStatus, entityId]);

  /**
   * The export is its own request, fired on the click — not fetched alongside the
   * report, same reasoning `ReportsLivestockInventory.tsx`'s own identical download
   * gives.
   */
  const [csvState, setCsvState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number } | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function downloadCsv(): Promise<void> {
    if (entityId === null) return;
    setCsvState({ kind: "busy" });
    try {
      const csv = await demoApi.getIncomeStatementCsv({ entityId, basis });

      // The bytes are the server writer's output verbatim — same "add a blob and a
      // filename and nothing else" discipline `ReportsLivestockInventory.tsx`'s own
      // download follows.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `easyfarm-income-statement-${basis}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const rows = Math.max(csv.trim().split("\n").length - 1, 0);
      setCsvState({ kind: "done", rows });
    } catch (err) {
      setCsvState({
        kind: "error",
        message: problemMessage(err, "Could not build the income statement file."),
      });
    }
  }

  function handleAllReportsClick(event: MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();
    // Self-contained, same device every other report screen's own handler already uses
    // rather than threading a `navigate` prop through every screen this job touches:
    // pushState doesn't fire popstate on its own, so App.tsx's existing listener is told
    // directly.
    window.history.pushState({}, "", "/reports");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  const income = report?.income ?? [];
  const expenses = report?.expenses ?? [];
  const isEmpty = status === "ready" && income.length === 0 && expenses.length === 0;

  return (
    <main className="income-statement">
      <PageHeader
        title="Income statement by entity"
        subtitle="Profit & loss for one entity, on a cash or accrual basis"
        action={
          <div className="income-statement__toggle" role="group" aria-label="Accounting basis">
            {BASES.map((b) => (
              <button
                key={b.key}
                type="button"
                aria-pressed={basis === b.key}
                className={`income-statement__toggle-btn${basis === b.key ? " income-statement__toggle-btn--on" : ""}`}
                onClick={() => setBasis(b.key)}
              >
                {b.label}
              </button>
            ))}
          </div>
        }
      />

      <a href="/reports" className="income-statement__all-reports" onClick={handleAllReportsClick}>
        ← All reports
      </a>

      <section
        className="income-statement__panel"
        aria-labelledby="income-statement-heading"
        data-state={isEmpty ? "empty" : status}
      >
        <div className="income-statement__head">
          <h2 id="income-statement-heading" className="income-statement__heading">
            Statement
          </h2>
          {/* Echoes report.basis — what the server says it COMPUTED — not the toggle
              state, same discipline ReportsStatements.tsx's own basis label keeps. */}
          <span className="income-statement__basis-label">
            {report ? `${report.basis} basis` : `${basis} basis`}
          </span>
        </div>

        {status === "loading" && (
          <StateMessage tone="loading" title="Loading the income statement…" />
        )}

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the income statement."
            detail={error ?? undefined}
          />
        )}

        {isEmpty && (
          <StateMessage
            tone="empty"
            title="No categorized activity yet."
            detail="Entries need an account (category) before they appear on this statement."
          />
        )}

        {status === "ready" && !isEmpty && report && (
          <>
            <div
              className="income-statement__table-wrap"
              tabIndex={0}
              role="group"
              aria-labelledby="income-statement-heading"
            >
              <table className="income-statement__table ruled-rows">
                <caption className="income-statement__caption">
                  {income.length + expenses.length} {income.length + expenses.length === 1 ? "line" : "lines"}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Number</th>
                    <th scope="col">Name</th>
                    <th scope="col" className="income-statement__num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="income-statement__section-row">
                    <th scope="rowgroup" colSpan={2}>Income</th>
                    <td className="income-statement__num income-statement__amount">
                      {formatMoney(report.incomeTotal)}
                    </td>
                  </tr>
                  {income.map((line) => (
                    <tr key={line.accountId}>
                      <td>{line.number}</td>
                      <th scope="row">{line.name}</th>
                      <td className="income-statement__num income-statement__amount">
                        {formatMoney(line.amount)}
                      </td>
                    </tr>
                  ))}

                  <tr className="income-statement__section-row">
                    <th scope="rowgroup" colSpan={2}>Expenses</th>
                    <td className="income-statement__num income-statement__amount">
                      {formatMoney(report.expenseTotal)}
                    </td>
                  </tr>
                  {expenses.map((line) => (
                    <tr key={line.accountId}>
                      <td>{line.number}</td>
                      <th scope="row">{line.name}</th>
                      <td className="income-statement__num income-statement__amount">
                        {formatMoney(line.amount)}
                      </td>
                    </tr>
                  ))}

                  <tr className="income-statement__section-row income-statement__section-row--net">
                    <th scope="rowgroup" colSpan={2}>Net income</th>
                    <td className="income-statement__num income-statement__amount">
                      {formatMoney(report.netIncome)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="income-statement__export">
              <h3 className="income-statement__export-title">Income statement for your records</h3>
              <p className="income-statement__export-copy">
                The same rows above, as a CSV a spreadsheet can open.
              </p>
              <button
                type="button"
                className="btn btn--secondary income-statement__export-btn"
                disabled={csvState.kind === "busy"}
                onClick={() => void downloadCsv()}
              >
                {csvState.kind === "busy" ? "Building the file…" : "Download CSV"}
              </button>

              {csvState.kind === "done" && (
                <p className="income-statement__export-result" role="status">
                  {csvState.rows} {csvState.rows === 1 ? "line" : "lines"} saved.
                </p>
              )}

              {csvState.kind === "error" && (
                <p className="income-statement__export-error" role="alert">
                  {csvState.message} Nothing was downloaded.
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
